// Screen — the shared app shell (§1.4).
// Sticky top bar (back, logo + title, streak & level chips), a single scrolling
// content region, and the 5-tab bottom nav (Home / Forge / Codex / Ranks / You).
// This replaces the old Header + two Sidebars + FooterControls + modal stack.
//
// Overlays (discovery celebration, completion, toast, chat, sheets) must be
// passed via the `overlay` prop so they anchor to the NON-scrolling shell root
// (position:absolute; inset:0) — never inside the scroll container. That fixes
// the "celebration invisible when the tray is scrolled" bug (§4.2).
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdBanner from "../ads/AdBanner";

export type TabKey = "home" | "forge" | "codex" | "ranks" | "you";

interface NavItem {
  key: TabKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const icon = (d: React.ReactNode) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);

const NAV: NavItem[] = [
  { key: "home", label: "Home", path: "/", icon: icon(<path d="M3 10.5L12 3l9 7.5M5 9.5V21h14V9.5" />) },
  { key: "forge", label: "Forge", path: "/play", icon: icon(<path d="M9 3h6M10 3v5.5L5 18a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 18l-5-9.5V3" />) },
  {
    key: "codex",
    label: "Codex",
    path: "/codex",
    icon: icon(
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
  },
  { key: "ranks", label: "Ranks", path: "/ranks", icon: icon(<path d="M8 21h8M12 17v4M6 4h12v4a6 6 0 0 1-12 0zM6 5H3v2a4 4 0 0 0 3 3.8M18 5h3v2a4 4 0 0 1-3 3.8" />) },
  {
    key: "you",
    label: "You",
    path: "/you",
    icon: icon(
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
  },
];

export interface ScreenProps {
  title: string;
  /** Which bottom-nav tab is highlighted. `null` = none (e.g. a sub-screen). */
  active?: TabKey | null;
  showBack?: boolean;
  onBack?: () => void;
  /** Hide the whole bottom nav (standalone pages: privacy, delete, auth). */
  hideNav?: boolean;
  /** Hide the streak/level chips (e.g. logged-out or standalone pages). */
  hideChips?: boolean;
  streak?: number;
  level?: number;
  /** AdSense slot id — renders a responsive banner just above the bottom nav. */
  ad?: string;
  /** Rendered absolutely over the shell root, above the scroll region. */
  overlay?: React.ReactNode;
  /** Extra content rendered inside the top bar, right of the chips. */
  topBarExtra?: React.ReactNode;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  children: React.ReactNode;
}

const Screen: React.FC<ScreenProps> = ({
  title,
  active = null,
  showBack = false,
  onBack,
  hideNav = false,
  hideChips = false,
  streak,
  level,
  ad,
  overlay,
  topBarExtra,
  contentClassName,
  contentStyle,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => (onBack ? onBack() : navigate(-1));

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    minHeight: 52,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    border: "none",
    cursor: "pointer",
    borderRadius: 14,
    color: isActive ? "var(--accent-text)" : "var(--text-3)",
    background: isActive ? "rgba(101,163,13,0.14)" : "transparent",
  });

  return (
    <div
      className="mc-shell"
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
        color: "var(--text)",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
        transition: "background .25s ease, color .25s ease",
      }}
    >
      {/* TOP BAR */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          paddingTop: "calc(14px + env(safe-area-inset-top))",
          background: "var(--topbar)",
          color: "var(--text)",
          borderBottom: "1px solid var(--border)",
          backdropFilter: "blur(8px)",
          zIndex: 5,
        }}
      >
        {showBack && (
          <button
            className="mc-tap"
            onClick={handleBack}
            aria-label="Back"
            style={{
              width: 38,
              height: 38,
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              borderRadius: 12,
              background: "var(--border)",
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 9 }}>
          <div
            style={{
              width: 26,
              height: 26,
              flex: "0 0 auto",
              borderRadius: 8,
              background: "radial-gradient(circle at 34% 28%, var(--accent-text), #65A30D 55%, #3F6212)",
              boxShadow: "0 3px 10px rgba(101,163,13,0.5)",
            }}
          />
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </span>
        </div>
        {!hideChips && (
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 999, background: "rgba(245,183,64,0.12)", border: "1px solid rgba(245,183,64,0.28)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#F5B740"><path d="M13.5 2L4 14h6l-1.5 8L18 10h-6z" /></svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#F5B740" }}>{streak ?? 0}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, background: "rgba(101,163,13,0.14)", border: "1px solid rgba(101,163,13,0.3)" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent-text)" }}>LVL</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{level ?? 1}</span>
            </div>
          </div>
        )}
        {topBarExtra}
      </div>

      {/* SCROLLING CONTENT */}
      <div
        className={`mc-scroll ${contentClassName ?? ""}`}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          background: "var(--bg)",
          color: "var(--text)",
          ...contentStyle,
        }}
      >
        {children}
      </div>

      {/* AD BANNER (above the bottom nav) */}
      {ad && (
        <div style={{ flex: "0 0 auto", background: "var(--nav)", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "center", overflow: "hidden" }}>
          <AdBanner adSlot={ad} adFormat="horizontal" fullWidthResponsive style={{ width: "100%", maxWidth: 728, maxHeight: 90 }} />
        </div>
      )}

      {/* BOTTOM NAV */}
      {!hideNav && (
        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "stretch",
            padding: 8,
            paddingBottom: "calc(8px + env(safe-area-inset-bottom))",
            gap: 2,
            background: "var(--nav)",
            color: "var(--text)",
            borderTop: "1px solid var(--border)",
            backdropFilter: "blur(10px)",
            zIndex: 6,
            boxShadow: "0 -6px 20px rgba(0,0,0,0.28)",
          }}
        >
          {NAV.map((item) => {
            const isActive = active
              ? active === item.key
              : item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.key}
                className="mc-tap"
                onClick={() => navigate(item.path)}
                style={navItemStyle(isActive)}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
              >
                {item.icon}
                <span style={{ fontSize: 10, fontWeight: 600 }}>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* OVERLAYS — anchored to the shell root, above the scroll region (§4.2) */}
      {overlay}
    </div>
  );
};

export default Screen;
